import { binToMeasurement } from 'features/fde/binningUtils'
import {
  CTP_STD_UNIT as STD_UNIT,
  normalizeBin,
  parseCtpEntry,
  resolveCtpResolution,
} from 'features/fde/ctpResolution'
import { FigureSelectionArea } from 'features/fde/regionTypes'
import {
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoMarkupLayer,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

const CTP_LINE_COLOR = '#1890ff'
/** Snap a bin coordinate to an integer when within floating-point noise of one. */
const FP_EPSILON = 1e-6

/**
 * Prefix applied to every markup-layer id this module emits. Use the helper
 * below rather than reading the constant directly so consumers (e.g. the viz
 * layer-visibility control) stay in lockstep with what this module produces.
 */
export const FFS_CTP_LAYER_PREFIX = 'ffs-triage-ctp_'

/** True iff `id` is a markup-layer id produced by `ctpEntryToLayer`. */
export const isCtpLayerId = (id: string): boolean =>
  id.startsWith(FFS_CTP_LAYER_PREFIX)

type ArcResult = 'STRAIGHT' | 'WRAP' | 'BREAK'

/** A folded arc's excluded region, in raster bins, normalized to `[0, P)`. */
interface ArcGap {
  startBin: number
  endBin: number
}

const snap = (v: number): number => {
  const rounded = Math.round(v)
  return Math.abs(v - rounded) < FP_EPSILON ? rounded : v
}

/**
 * Does the direct (non-wrapping) interval `[lo, hi]` touch the open gap
 * `(gap.endBin, gap.startBin)`? The gap is a cyclic interval in `[0, P)`: it
 * runs contiguously between the two bounds when `endBin < startBin`, and
 * wraps through the `0`/`P` seam when `endBin > startBin` (in which case the
 * arc itself -- `[startBin, endBin]` -- is the contiguous side). A degenerate
 * zero-width gap (`startBin === endBin`) is treated as "no gap" -- nothing
 * crosses it.
 */
const directCrossesGap = (lo: number, hi: number, gap: ArcGap): boolean => {
  const { startBin, endBin } = gap
  if (endBin === startBin) return false
  if (endBin < startBin) return lo < startBin && hi > endBin
  return lo < startBin || hi > endBin
}

/**
 * Converts a single `FFS_CRITICAL_THICKNESS_PROFILE` Rolodex entry into a
 * line-primitive markup layer.
 *
 * Data contract (confirmed with backend):
 *   - `entry.data.critical_path_bins` is `[binX, binY][]`, monotonic along
 *     `entry.data.index_axis` (steps +1 per covered row/col). A step > 1 on
 *     that axis is the no-data marker: it denotes a dropped all-NaN run, so
 *     the segment between `critical_path_bins[i]` and `[i+1]` is broken.
 *   - `entry.data.wrap_axis` / `entry.data.period_bins` are present only for
 *     wrap-enabled (periodic) components; wrap-axis coordinates may run past
 *     `period_bins` for seam-crossing runs (still monotonic, never a max->0
 *     jump).
 *   - `selection` (the matching indication's folded region, `end < start`
 *     when it wraps) is the LTA's arc on the SAME periodic frame as
 *     `critical_path_bins`. For a y-CTP (`index_axis !== wrap_axis`), a
 *     segment whose direct path crosses the gap outside that arc is either
 *     re-routed the short way around the seam (WRAP) or, if the seam route
 *     isn't shorter, dropped (BREAK) rather than drawn across the gap. An
 *     x-CTP (`index_axis === wrap_axis`) never arc-breaks -- it only wraps at
 *     a genuine period-boundary crossing.
 *   - `critical_path_bins` is indexed on whatever grid the backend generated
 *     the profile against, which is NOT necessarily `cdf`'s grid. When
 *     present, `entry.data.bin_width` / `bin_height` / `x_start` / `y_start`
 *     (the backend's explicit resolution stamp) are used instead of `cdf` --
 *     see `resolveCtpResolution`.
 *
 * Markup layer shape mirrors `criticalProfileMarkup.ts:64-83`:
 *   data.primitives is a Record<string, Primitive> MAP (NOT an array), with
 *   one `crit_prof_<runIndex>: { kind: 'line', points: [{x, y}, ...] }` entry
 *   per contiguous (break-free) run of bins. `runIndex` comes from a single
 *   counter incremented only when a run is actually emitted (a run collapsed
 *   to <2 points is dropped, not counted).
 *
 * Returns `[]` if the resolution can't be resolved (no explicit fields and
 * no usable `cdf`), there are fewer than 2 bins, or every run collapses to a
 * single point.
 */
export function ctpEntryToLayer(
  entry: GeckoEntry,
  cdf: GeckoComponentDataFrame | undefined,
  /** The matching indication's folded selection, i.e. the LTA's arc. */
  selection: FigureSelectionArea | undefined,
  /** 1-based position, used to synthesize a readable name when untitled. */
  ordinal?: number
): GeckoMarkupLayer[] {
  const ctp = parseCtpEntry(entry)
  const { bins, period: P, wrapIdx, indexIdx, hasWrap } = ctp
  if (bins.length < 2) return []

  try {
    // Measurement/CDF reads can throw on malformed data. Keep them inside
    // the try so bad data skips this CTP rather than crashing the triage
    // step.
    const resolution = resolveCtpResolution(ctp, cdf)
    if (!resolution) return []
    const { binWidth, binHeight, geomStartX, geomStartY } = resolution
    const geomStart = { x: geomStartX, y: geomStartY }
    const binSize = { x: binWidth, y: binHeight }

    const norm = (v: number): number => normalizeBin(v, P)

    // The LTA's arc, read from the folded indication selection on the same
    // periodic (raster-bin) frame as `critical_path_bins`. `undefined` means
    // "no gap known" -- either the region spans the whole period (no
    // excluded zone) or no selection was resolved for this CTP.
    let arcGap: ArcGap | undefined
    let fullPeriod = false
    if (hasWrap && selection) {
      const axis = ctp.wrapAxis as 'x' | 'y'
      const startBinC = snap(
        (selection[axis].start.convertTo(STD_UNIT).num - geomStart[axis]) /
          binSize[axis]
      )
      const endBinC = snap(
        (selection[axis].end.convertTo(STD_UNIT).num - geomStart[axis]) /
          binSize[axis]
      )
      const wrappedSel = endBinC < startBinC
      // A non-wrapped selection whose pre-modulo span is ~P covers the whole
      // circumference -- there's no excluded region. Detected BEFORE
      // normalizing (mod'ing `endBin=P` down to `0` would be indistinguishable
      // from a zero-width selection).
      fullPeriod = !wrappedSel && Math.abs(endBinC - startBinC - P) < FP_EPSILON
      if (!fullPeriod) {
        arcGap = { startBin: norm(startBinC), endBin: norm(endBinC) }
      }
    }

    /** Per-segment arc classification for `a -> b` (raw, unnormalized bins). */
    const classifyArc = (
      a: [number, number],
      b: [number, number]
    ): ArcResult => {
      if (!hasWrap) return 'STRAIGHT'
      if (ctp.indexAxis === ctp.wrapAxis) {
        // x-CTP: never arc-break on the selection gap -- only wrap at a
        // genuine period-boundary crossing.
        const wraps = Math.floor(a[wrapIdx] / P) !== Math.floor(b[wrapIdx] / P)
        return wraps ? 'WRAP' : 'STRAIGHT'
      }
      // y-CTP: needs a resolved arc to reason about the gap at all.
      if (!selection) return 'STRAIGHT'
      const aw = norm(a[wrapIdx])
      const bw = norm(b[wrapIdx])
      if (fullPeriod || !arcGap) {
        // No excluded region -- only seam drift (the short way) applies.
        return Math.abs(aw - bw) > P / 2 ? 'WRAP' : 'STRAIGHT'
      }
      const lo = Math.min(aw, bw)
      const hi = Math.max(aw, bw)
      if (!directCrossesGap(lo, hi, arcGap)) return 'STRAIGHT'
      const directDistance = hi - lo
      const seamDistance = P - directDistance
      return seamDistance < directDistance ? 'WRAP' : 'BREAK'
    }

    const toPoint = ([bx, by]: [number, number]) => {
      const x = hasWrap && wrapIdx === 0 ? norm(bx) : bx
      const y = hasWrap && wrapIdx === 1 ? norm(by) : by
      return {
        x: binToMeasurement(x, geomStartX, binWidth),
        y: binToMeasurement(y, geomStartY, binHeight),
      }
    }

    const primitives: Record<string, unknown> = {}
    let runCounter = 0
    let currentPoints: [number, number][] = [bins[0]]
    let currentWraps: ('x' | 'y')[][] = []

    const flush = () => {
      if (currentPoints.length >= 2) {
        const wraps = currentWraps.some((w) => w.length > 0)
          ? currentWraps
          : undefined
        primitives[`crit_prof_${runCounter}`] = {
          kind: 'line',
          points: currentPoints.map(toPoint),
          ...(wraps ? { wraps } : {}),
        }
        runCounter++
      }
      currentPoints = []
      currentWraps = []
    }

    for (let i = 0; i < bins.length - 1; i++) {
      const a = bins[i]
      const b = bins[i + 1]
      const isNoDataBreak = b[indexIdx] - a[indexIdx] > 1
      const arcResult = classifyArc(a, b)
      const isBreak = isNoDataBreak || arcResult === 'BREAK'

      if (isBreak) {
        flush()
        currentPoints.push(b)
      } else {
        currentPoints.push(b)
        currentWraps.push(
          arcResult === 'WRAP' ? [ctp.wrapAxis as 'x' | 'y'] : []
        )
      }
    }
    flush()

    if (Object.keys(primitives).length === 0) return []

    const geometry = {
      x_measurement_unit: STD_UNIT.id,
      y_measurement_unit: STD_UNIT.id,
    }

    const json: RolodexEntryJson = {
      id: `${FFS_CTP_LAYER_PREFIX}${entry.id}`,
      type: RolodexEntryType.MARKUP_LAYER,
      tags: entry.tags,
      data: {
        name:
          ctp.title ?? (ordinal !== undefined ? `CTP ${ordinal}` : entry.id),
        path: ['FFS LTA Paths'],
        customer_visible: true,
        color: CTP_LINE_COLOR,
        category: 'user-added',
        geometry,
        primitives,
      },
      index: {},
      generation: 1,
      updated_at: new Date(0).toISOString(),
      access_restriction: null,
    }

    return [GeckoMarkupLayer.CreateFromJSON(json)]
  } catch (err) {
    console.warn(`Failed to construct CTP markup layer ${entry.id}:`, err)
    return []
  }
}
